import pusherHelper from './pusher/pusher.helper';
import { ui } from '@/api';

export default function pusherServiceFactory(services) {
  let pusherClient;
  let channelEventBindings = {};

  let thisService = {
    bind,
    unbind,
    getPusherClient,
  };

  activate();
  return thisService;

  ////////////////////

  function activate() {
    let ph = pusherHelper;
    if (!ph) {
      console.warn('PrintOS Header: Pusher helper not found');
      return;
    }

    pusherHelper.enablePusherWithCredentials();
    //publishGlobalApi();
  }

  function publishGlobalApi() {
    ui.pusher = {
      bind,
      unbind,
    };
  }

  function getPusherClient() {
    if (!pusherHelper) {
      console.warn('PrintOS Header: Cannot get Pusher Client (no helper)');
      return Promise.reject(null);
    }

    if (pusherClient) {
      return Promise.resolve(pusherClient);
    }

    return services.registry.getRegistry().then(data => {
      // Check again to ensure there weren't multiple calls
      // (seems like an equivalent of double-check locking)
      if (pusherClient) {
        return Promise.resolve(pusherClient);
      }

      let pusherKey = data.keys.pusherUiKey;
      if (!pusherKey) {
        return Promise.reject('Unable to get pusher UI key (may be unauthenticated)');
      }

      let authEndpoint = `${data.services.aaa.base_sv_url}v4/client-eventing/authorize-subscription-request/ui`;

      pusherClient = pusherHelper.createPusherInstance(pusherKey, authEndpoint);
      return pusherClient;
    });
  }

  /**
   * @description Binds a callback function to a channel event.
   * @param {string} channelName of the channel to bind to
   * @param {string} eventName of the event to bind to
   * @param {function} callback for pusher to call
   * @returns {Promise.<T>|IPromise<TResult>} a promise
   */
  function bind(channelName, eventName, callback) {
    if(!channelName) {
      return Promise.reject('Unable to bind without a channel name');
    }
    if(!eventName) {
      return Promise.reject('Unable to bind without an event name');
    }
    if(!callback) {
      return Promise.reject('Unable to bind without a callback');
    }

    return getPusherClient().then(pusherClient => {
      let channel = getSubscribedChannel(pusherClient, channelName);

      let decoratedCallback = function pusherCallback(payload) {
        callback(payload);
        // With angular, we had to call run a digest cycle here now.
        // Is there anything else we should do here, or should blue not decorate the pusher callback?
      };

      channel.bind(eventName, decoratedCallback);
      // Gross - save a reference back to the decorated callback.
      // It is this callback that is needed in unbind (see pusher-angular.unbind).
      callback.decorator = decoratedCallback;

      // Should we save the callback or the decorated callback?
      // Each instance of the decorated callback will be unique,
      // but it still seems more straightforward to track the callback instead of the decorated callback.
      addChannelEventBinding(channelName, eventName, callback);

      //services.ga.eventTrack(ANALYTICS_CATEGORY, `${services.app.getCurrentAppId()} - bind - ${channelName} - ${eventName}`, lastSocketId);
    });
  }

  function getSubscribedChannel(pusherClient, channelName) {
    if (channelEventBindings[channelName]) {
      let channel = pusherClient.channels.find(channelName);
      if (channel) {
        // We've already subscribed to this channel.
        return channel;
      }
    }
    return pusherClient.subscribe(channelName);
  }

  /**
   * @description Unbinds a callback function from a channel event.
   * @param {string} channelName of the channel to unbind from
   * @param {string} eventName of the event to unbind from
   * @param {function} callback to unbind
   * @returns {Promise.<T>|IPromise<TResult>} a promise
   */
  function unbind(channelName, eventName, callback) {
    if(!channelName) {
      return Promise.reject('Unable to unbind without a channel name');
    }
    if(!eventName) {
      return Promise.reject('Unable to unbind without an event name');
    }
    if(!callback) {
      return Promise.reject('Unable to unbind without a callback');
    }

    return getPusherClient().then(pusherClient => {
      let channel = pusherClient.channels.find(channelName);
      if (!channel) {
        return Promise.reject(`Unable to find channel to unbind from ${channelName}`);
      }

      let decoratedCallback = callback.decorator;
      channel.unbind(eventName, decoratedCallback);
      removeChannelEventBinding(channelName, eventName, callback);
      if (!channelEventBindings[channelName]) {
        pusherClient.unsubscribe(channelName);
      }

      //services.ga.eventTrack(ANALYTICS_CATEGORY, `${services.app.getCurrentAppId()} - unbind - ${channelName} - ${eventName}`, lastSocketId);
    });
  }

  function addChannelEventBinding(channelName, eventName, callback) {
    channelEventBindings[channelName] = channelEventBindings[channelName] || {};
    channelEventBindings[channelName][eventName] = channelEventBindings[channelName][eventName] || [];
    channelEventBindings[channelName][eventName].push(callback);
  }

  function removeChannelEventBinding(channelName, eventName, callback) {
    let channelEvents = channelEventBindings[channelName];
    if (!channelEvents) {
      return;
    }
    if (!channelEvents[eventName]) {
      return;
    }

    channelEvents[eventName] = without(channelEvents[eventName], callback);
    if (channelEvents[eventName].length === 0) {
      delete channelEvents[eventName];
    }
    if (isEmptyObject(channelEvents)) {
      delete channelEventBindings[channelName];
    }
  }

  function isEmptyObject(obj) {
    return Object.getOwnPropertyNames(obj).length === 0;
  }

  function without(array, value) {
    return array.filter(item => {
      return item !== value;
    });
  }
}
